import { AISHA_API_BASE } from '@/constants';

export interface ArtisanProductVariation {
  name: string;
  price: number;
  installation_price: number;
  wholesale_price: number;
  wholesale_installation_price: number;
  sample_image_url: string;
}

export interface ArtisanProduct {
  id: number;
  product_name: string;
  compatible_maker: string;
  compatible_car: string;
  compatible_model: string;
  compatible_notes: string;
  description: string;
  product_price: number;
  installation_description: string;
  installation_price: number;
  installation_estimated_time: string;
  wholesale_price: number;
  wholesale_installation_price: number;
  sample_image_url: string;
  sample_image_url_2: string;
  variations: ArtisanProductVariation[];
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export type ArtisanProductCreateUpdate = Omit<ArtisanProduct, 'id' | 'created_at' | 'updated_at'>;

// Artisanサービス品一覧を取得
export const getArtisanProducts = async (): Promise<ArtisanProduct[]> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisanサービス品一覧の取得に失敗しました');
    }
  } catch (error) {
    console.error('Artisanサービス品一覧取得エラー:', error);
    throw error;
  }
};

// Artisanサービス品詳細を取得
export const getArtisanProduct = async (id: number): Promise<ArtisanProduct> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisanサービス品詳細の取得に失敗しました');
    }
  } catch (error) {
    console.error('Artisanサービス品詳細取得エラー:', error);
    throw error;
  }
};

// Artisanサービス品を作成
export const createArtisanProduct = async (data: ArtisanProductCreateUpdate): Promise<ArtisanProduct> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan/create/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisanサービス品の作成に失敗しました');
    }
  } catch (error) {
    console.error('Artisanサービス品作成エラー:', error);
    throw error;
  }
};

// Artisanサービス品を更新
export const updateArtisanProduct = async (id: number, data: ArtisanProductCreateUpdate): Promise<ArtisanProduct> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan/${id}/update/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Artisanサービス品の更新に失敗しました');
    }
  } catch (error) {
    console.error('Artisanサービス品更新エラー:', error);
    throw error;
  }
};

// Artisanサービス品を削除
export const deleteArtisanProduct = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/admin/artisan/${id}/delete/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('firebase_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Artisanサービス品の削除に失敗しました');
    }
  } catch (error) {
    console.error('Artisanサービス品削除エラー:', error);
    throw error;
  }
};

// 公開Artisanサービス品一覧を取得（認証不要）
export const getPublicArtisanProducts = async (): Promise<ArtisanProduct[]> => {
  try {
    const response = await fetch(`${AISHA_API_BASE}/artisan/public/?public_only=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error('公開Artisanサービス品取得エラー:', error);
    return [];
  }
};
